import { http } from "./http";

export type MaintenanceItem = {
  id: string;
  workOrderId: string;
  taskId?: string;
  title: string;
  instructions?: string | null;
  isDone: boolean;
  doneAt?: string | null;
};

export type MaintenanceWorkOrder = {
  id: string;
  machineId: string;
  planId?: string | null;
  title: string;
  description?: string | null;
  type: string;
  priority: string;
  status: string;
  dueAt?: string | null;
  openedAt: string;
  startedAt?: string | null;
  machine: { id: string; name: string };
  plan?: { id: string; name: string } | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  items: MaintenanceItem[];
};

export type MaintenanceTask = {
  id: string;
  planId: string;
  title: string;
  instructions?: string | null;
  orderIndex: number;
};

export type MaintenancePlan = {
  id: string;
  machineId: string;
  name: string;
  description?: string | null;
  type: string;
  trigger: string;
  priority: string;
  everyDays?: number | null;
  everyCycles?: number | null;
  everyHours?: number | null;
  lastDoneAt?: string | null;
  isActive: boolean;
  machine: { id: string; name: string };
  tasks: MaintenanceTask[];
};

export type PlanDueSoon = {
  plan: MaintenancePlan;
  daysUntilDue: number | null;
  cyclesUntilDue: number | null;
  nextDueAt: string | null;
  severity: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
};

export type MaintenanceDashboard = {
  openWorkOrders: MaintenanceWorkOrder[];
  plansDueSoon: PlanDueSoon[];
  totalPlans: number;
  totalOpen: number;
  alertsCount: number;
};

export const maintenanceApi = {
  getDashboard: (token: string | null) =>
    http<MaintenanceDashboard>("/maintenance/dashboard", { method: "GET" }, token),

  listWorkOrders: (
    token: string | null,
    params?: { status?: string; machineId?: string }
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.machineId) qs.set("machineId", params.machineId);
    const query = qs.toString();
    return http<MaintenanceWorkOrder[]>(
      `/maintenance/work-orders${query ? `?${query}` : ""}`,
      { method: "GET" },
      token
    );
  },

  getWorkOrder: (token: string | null, id: string) =>
    http<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}`, { method: "GET" }, token),

  deleteWorkOrder: (token: string | null, id: string) =>
    http<{ ok: boolean }>(`/maintenance/work-orders/${id}`, { method: "DELETE" }, token),

  confirmPlanDone: (token: string | null, id: string) =>
    http<MaintenancePlan>(`/maintenance/plans/${id}/confirm-done`, { method: "POST" }, token),

  listPlans: (token: string | null, params?: { machineId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.machineId) qs.set("machineId", params.machineId);
    const query = qs.toString();
    return http<MaintenancePlan[]>(
      `/maintenance/plans${query ? `?${query}` : ""}`,
      { method: "GET" },
      token
    );
  },

  getPlan: (token: string | null, id: string) =>
    http<MaintenancePlan>(`/maintenance/plans/${id}`, { method: "GET" }, token),

  createPlan: (
    token: string | null,
    dto: {
      machineId: string;
      name: string;
      description?: string;
      trigger: string;
      everyDays?: number;
      priority?: string;
    }
  ) =>
    http<MaintenancePlan>("/maintenance/plans", {
      method: "POST",
      body: JSON.stringify(dto),
    }, token),

  createTask: (
    token: string | null,
    planId: string,
    dto: { title: string; instructions?: string; orderIndex?: number }
  ) =>
    http<MaintenanceTask>(`/maintenance/plans/${planId}/tasks`, {
      method: "POST",
      body: JSON.stringify(dto),
    }, token),

  deletePlan: (token: string | null, id: string) =>
    http<{ id: string }>(`/maintenance/plans/${id}`, { method: "DELETE" }, token),
};
